import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MessageCircle, Search, Users, X } from "lucide-react";
import { clsx } from "clsx";
import apiClient from "@/api/client";
import { useAuthStore } from "@/store/auth";
import { useChatRooms, useCreateRoom } from "../api/chatApi";

interface InternalUser {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  role: string;
  branch: { id: string; name: string } | null;
  profile_picture_url: string | null;
  contractor_company_name?: string | null;
}

interface NewRoomDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (roomId: string) => void;
}

function initials(u: InternalUser): string {
  const a = (u.first_name || "")[0];
  const b = (u.last_name || "")[0];
  return ((a ?? "") + (b ?? "")).toUpperCase() || u.username[0].toUpperCase();
}

export function NewRoomDialog({
  open,
  onClose,
  onCreated,
}: NewRoomDialogProps) {
  const me = useAuthStore((s) => s.user);
  const [kind, setKind] = useState<"direct" | "group">("direct");
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const create = useCreateRoom();
  const myRooms = useChatRooms();

  // Users I already have an active DIRECT room with — hide them when starting
  // a new DM (the backend enforces one-per-pair, but the picker shouldn't
  // pretend otherwise).
  const existingDirectPartners = useMemo(() => {
    const set = new Set<string>();
    for (const r of myRooms.data ?? []) {
      if (r.kind !== "direct") continue;
      for (const m of r.members ?? []) {
        if (m.id !== me?.id) set.add(m.id);
      }
    }
    return set;
  }, [myRooms.data, me?.id]);

  const users = useQuery({
    queryKey: ["chat", "selectable-users"],
    enabled: open,
    queryFn: async () => {
      // `selectable=chat` dokłada podwykonawców wpuszczonych do GG przez CEO.
      const r = await apiClient.get<{ data: InternalUser[] }>(
        "/auth/users/?selectable=chat",
      );
      return r.data.data ?? [];
    },
  });

  const candidates = useMemo(() => {
    const list = users.data ?? [];
    const term = search.trim().toLowerCase();
    return list
      .filter((u) => u.id !== me?.id)
      .filter((u) => kind !== "direct" || !existingDirectPartners.has(u.id))
      .filter((u) => {
        if (!term) return true;
        return (
          u.username.toLowerCase().includes(term) ||
          `${u.first_name} ${u.last_name}`.toLowerCase().includes(term) ||
          (u.branch?.name ?? "").toLowerCase().includes(term) ||
          (u.contractor_company_name ?? "").toLowerCase().includes(term)
        );
      })
      .sort((a, b) => {
        // Pracownicy GK najpierw — podwykonawca to wyjątek, nie pozycja, na którą
        // wpada się przypadkiem szukając kolegi z biura.
        const ac = a.role === "contractor" ? 1 : 0;
        const bc = b.role === "contractor" ? 1 : 0;
        if (ac !== bc) return ac - bc;
        const an = `${a.first_name} ${a.last_name}`.trim() || a.username;
        const bn = `${b.first_name} ${b.last_name}`.trim() || b.username;
        return an.localeCompare(bn, "pl");
      });
  }, [users.data, me?.id, search, kind, existingDirectPartners]);

  function toggle(uid: string) {
    if (kind === "direct") {
      setSelected([uid]);
    } else {
      setSelected((prev) =>
        prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid],
      );
    }
  }

  function reset() {
    setKind("direct");
    setName("");
    setSelected([]);
    setSearch("");
  }

  async function submit() {
    if (selected.length === 0) return;
    if (kind === "group" && !name.trim()) return;
    const room = await create.mutateAsync({
      kind,
      name: kind === "group" ? name.trim() : "",
      member_ids: selected,
    });
    onCreated(room.id);
    reset();
  }

  if (!open) return null;

  const selectedSet = new Set(selected);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={() => {
        reset();
        onClose();
      }}
    >
      <div
        className="bg-white border border-gray-200 rounded-lg w-full max-w-md shadow-2xl flex flex-col max-h-[85vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-mono font-semibold text-gray-900">
            Nowa rozmowa
          </h3>
          <button
            type="button"
            onClick={() => {
              reset();
              onClose();
            }}
            className="p-1 hover:bg-gray-100 rounded text-gray-500"
          >
            <X size={16} />
          </button>
        </div>

        {/* Type switch */}
        <div className="grid grid-cols-2 border-b border-gray-200">
          <button
            type="button"
            onClick={() => {
              setKind("direct");
              setSelected([]);
            }}
            className={clsx(
              "flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors",
              kind === "direct"
                ? "bg-amber-50 text-amber-700 border-b-2 border-amber-500"
                : "text-gray-600 hover:bg-gray-50 border-b-2 border-transparent",
            )}
          >
            <MessageCircle size={14} /> Rozmowa 1-1
          </button>
          <button
            type="button"
            onClick={() => {
              setKind("group");
              setSelected([]);
            }}
            className={clsx(
              "flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors",
              kind === "group"
                ? "bg-amber-50 text-amber-700 border-b-2 border-amber-500"
                : "text-gray-600 hover:bg-gray-50 border-b-2 border-transparent",
            )}
          >
            <Users size={14} /> Grupowa
          </button>
        </div>

        {kind === "group" && (
          <div className="px-4 pt-3">
            <label className="block text-[11px] font-medium text-gray-600 mb-1">
              Nazwa pokoju
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="np. „Projekty wykonawcze - STI”"
              className="w-full rounded-md border border-gray-300 bg-white text-sm px-3 py-2 text-gray-900 focus:outline-none focus:border-amber-400"
            />
          </div>
        )}

        <div className="px-4 pt-3">
          <label className="block text-[11px] font-medium text-gray-600 mb-1">
            {kind === "direct" ? "Wybierz osobę" : "Dodaj uczestników"}
          </label>
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Szukaj po imieniu, nazwisku, oddziale lub firmie…"
              className="w-full rounded-md border border-gray-300 bg-white text-sm pl-7 pr-3 py-2 text-gray-900 focus:outline-none focus:border-amber-400"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-2">
          <div className="border border-gray-200 rounded-md overflow-hidden">
            {users.isLoading && (
              <div className="p-3 text-xs text-gray-500">Wczytuję…</div>
            )}
            {candidates.map((u) => {
              const full =
                `${u.first_name} ${u.last_name}`.trim() || u.username;
              const checked = selectedSet.has(u.id);
              return (
                <label
                  key={u.id}
                  className={clsx(
                    "flex items-center gap-2 px-3 py-2 cursor-pointer text-sm border-b border-gray-100 last:border-b-0",
                    checked ? "bg-amber-50" : "hover:bg-gray-50",
                  )}
                >
                  <input
                    type={kind === "direct" ? "radio" : "checkbox"}
                    name={kind === "direct" ? "member" : undefined}
                    checked={checked}
                    onChange={() => toggle(u.id)}
                    className="accent-amber-500"
                  />
                  {u.profile_picture_url ? (
                    <img
                      src={u.profile_picture_url}
                      alt={full}
                      className="w-7 h-7 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-700 font-mono text-[11px] font-semibold flex items-center justify-center">
                      {initials(u)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-900 font-medium truncate">
                      {full}
                    </div>
                    {u.role === "contractor" ? (
                      <div className="text-[11px] text-amber-700 truncate">
                        Podwykonawca
                        {u.contractor_company_name
                          ? ` · ${u.contractor_company_name}`
                          : ""}
                      </div>
                    ) : (
                      u.branch?.name && (
                        <div className="text-[11px] text-gray-500 truncate">
                          {u.branch.name}
                        </div>
                      )
                    )}
                  </div>
                </label>
              );
            })}
            {!users.isLoading && candidates.length === 0 && (
              <div className="p-3 text-xs text-gray-500">
                Brak pasujących użytkowników.
              </div>
            )}
          </div>
          {kind === "group" && selected.length > 0 && (
            <div className="text-[11px] text-gray-500 mt-2">
              Wybrano: {selected.length}{" "}
              {selected.length === 1
                ? "osoba"
                : selected.length < 5
                  ? "osoby"
                  : "osób"}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2 bg-gray-50">
          <button
            type="button"
            onClick={() => {
              reset();
              onClose();
            }}
            className="px-3 py-1.5 text-sm rounded-md hover:bg-gray-100 text-gray-700"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={
              create.isPending ||
              selected.length === 0 ||
              (kind === "group" && !name.trim())
            }
            className="px-3 py-1.5 text-sm rounded-md bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 font-medium"
          >
            {create.isPending ? "Tworzenie…" : "Utwórz"}
          </button>
        </div>
      </div>
    </div>
  );
}
